import { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Edit, Plus, Trash2, XCircle } from 'lucide-react';
import { addDoc, collection, doc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ERPUser } from '../../core/auth/types';
import { db } from '../../core/firebase/firebase';
import { Inquiry } from './types';
import { createInquiry, deleteInquiry, findDuplicateWarning, listInquiries, setInquiryStage, updateInquiry } from './crmService';

type CrmStaffOption = {
  uid: string;
  fullName: string;
  email: string;
  role: string;
  departments: string[];
  primaryDepartment: string;
  isActive: boolean;
};

type ClientOption = {
  id: string;
  fullName: string;
  phone: string;
  email: string;
  usCity: string;
  indiaCity: string;
};

const emptyForm: Partial<Inquiry> = {
  clientId: '',
  clientName: '',
  phone: '',
  email: '',
  serviceType: 'package',
  department: 'package',
  destination: '',
  travelStartDate: '',
  travelEndDate: '',
  returnDate: '',
  totalPax: 1,
  adults: 0,
  children: 0,
  seniors: 0,
  budget: 0,
  quotedAmount: 0,
  source: 'manual',
  stage: 'new',
  priority: 'medium',
  assignedUserId: '',
  assignedUserName: '',
  nextFollowUpDate: '',
  waitingReason: '',
  clientRequestNote: '',
  internalNote: ''
};

function cleanDept(value: unknown) {
  return String(value || '').toLowerCase().replace(/[^a-z_]/g, '').trim();
}

function normalizeCrmStaff(id: string, raw: any): CrmStaffOption {
  const primaryDepartment = cleanDept(raw.primaryDepartment || raw.department || 'general');
  const departments = Array.isArray(raw.departments) && raw.departments.length
    ? raw.departments.map(cleanDept)
    : [primaryDepartment];

  return {
    uid: String(raw.uid || id),
    fullName: String(raw.fullName || raw.name || raw.email || id),
    email: String(raw.email || ''),
    role: cleanDept(raw.role || 'staff'),
    departments,
    primaryDepartment,
    isActive: raw.isActive !== false && raw.status !== 'inactive'
  };
}

function normalizeClient(id: string, raw: any): ClientOption {
  const first = String(raw.firstName || '').trim();
  const last = String(raw.lastName || '').trim();
  const fullName = String(raw.fullName || raw.name || `${first} ${last}`.trim() || 'Unnamed Client');

  return {
    id,
    fullName,
    phone: String(raw.phoneFull || raw.phoneNumber || raw.phone || ''),
    email: String(raw.email || ''),
    usCity: String(raw.usCity || ''),
    indiaCity: String(raw.indiaCity || raw.indianCity || '')
  };
}

function userCanSeeDepartment(user: ERPUser, department: string) {
  if (user.role === 'super_admin' || user.role === 'admin') return true;
  const wanted = cleanDept(department);
  const userDepartments = Array.isArray((user as any).departments)
    ? (user as any).departments.map(cleanDept)
    : [cleanDept((user as any).department || 'general')];

  return userDepartments.includes(wanted) || userDepartments.includes('general');
}

function staffForDepartment(staffList: CrmStaffOption[], department: string) {
  const wanted = cleanDept(department || 'general');
  return staffList.filter((staff) => {
    if (!staff.isActive) return false;
    if (staff.role === 'super_admin' || staff.role === 'admin') return true;
    return staff.primaryDepartment === wanted || staff.departments.includes(wanted);
  });
}

function validate(form: Partial<Inquiry>) {
  if (!form.clientName?.trim()) return 'Client name is required.';
  if (form.stage === 'waiting' && !form.waitingReason?.trim()) return 'Waiting reason is required when stage is Waiting.';
  if (form.travelStartDate && form.travelEndDate && form.travelEndDate < form.travelStartDate) return 'Trip / Service End Date cannot be earlier than Travel Start Date.';
  if (form.travelStartDate && form.returnDate && form.returnDate < form.travelStartDate) return 'Return Flight Date cannot be earlier than Travel Start Date.';
  return '';
}

function makeBookingCodeFromInquiry() {
  return `BK-${Math.floor(100000 + Math.random() * 900000)}`;
}

function money(value: unknown) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

async function createBookingFromInquiry(inquiry: Inquiry, user: ERPUser) {
  if (inquiry.convertedBookingId) {
    alert('This inquiry is already converted to booking.');
    return inquiry.convertedBookingId;
  }

  const sellAmount = money(inquiry.quotedAmount || 0);
  const netAmount = money((inquiry as any).netAmount || 0);
  const receivedAmount = money((inquiry as any).receivedAmount || 0);
  const supplierPaidAmount = money((inquiry as any).supplierPaidAmount || 0);

  const bookingPayload = {
    bookingCode: makeBookingCodeFromInquiry(),
    inquiryId: inquiry.id,
    clientId: inquiry.clientId || '',
    clientName: inquiry.clientSnapshot?.name || inquiry.clientName || '',
    clientPhone: inquiry.clientSnapshot?.phone || inquiry.phone || '',
    supplierId: (inquiry as any).supplierId || '',
    supplierName: (inquiry as any).supplierName || '',
    serviceType: inquiry.serviceType || 'package',
    department: inquiry.department || 'general',
    destination: inquiry.destination || '',
    travelStartDate: inquiry.travelStartDate || '',
    travelEndDate: inquiry.travelEndDate || '',
    returnDate: inquiry.returnDate || inquiry.travelEndDate || '',
    passengers: Number(inquiry.totalPax || 1),
    sellAmount,
    netAmount,
    receivedAmount,
    supplierPaidAmount,
    profit: sellAmount - netAmount,
    outstandingAmount: Math.max(sellAmount - receivedAmount, 0),
    supplierBalance: Math.max(netAmount - supplierPaidAmount, 0),
    bookingStatus: 'draft',
    paymentStatus: receivedAmount >= sellAmount && sellAmount > 0 ? 'paid' : receivedAmount > 0 ? 'partial' : 'unpaid',
    supplierPaymentStatus: supplierPaidAmount >= netAmount && netAmount > 0 ? 'paid' : supplierPaidAmount > 0 ? 'partial' : 'unpaid',
    notes: inquiry.internalNote || inquiry.clientRequestNote || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: user.uid,
    updatedBy: user.uid
  };

  const bookingRef = await addDoc(collection(db, 'bookings'), bookingPayload);

  await updateDoc(doc(db, 'inquiries', inquiry.id), {
    stage: 'converted',
    status: 'converted',
    convertedBookingId: bookingRef.id,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });

  alert('Inquiry converted to Booking successfully.');
  window.history.pushState(null, '', '/bookings');
  window.dispatchEvent(new PopStateEvent('popstate'));

  return bookingRef.id;
}

export function CrmPage({ user }: { user: ERPUser }) {
  const [items, setItems] = useState<Inquiry[]>([]);
  const [staffOptions, setStaffOptions] = useState<CrmStaffOption[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Inquiry | null>(null);
  const [form, setForm] = useState<Partial<Inquiry>>(emptyForm);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function loadInquiries() {
    const rows = await listInquiries();
    if (user.role === 'super_admin' || user.role === 'admin') {
      setItems(rows);
      return;
    }

    setItems(rows.filter((item) =>
      item.assignedUserId === user.uid ||
      userCanSeeDepartment(user, item.department)
    ));
  }

  async function loadStaff() {
    const snap = await getDocs(collection(db, 'users'));
    setStaffOptions(snap.docs.map((d) => normalizeCrmStaff(d.id, d.data())));
  }

  async function loadClients() {
    const snap = await getDocs(collection(db, 'clients'));
    setClients(snap.docs.map((d) => normalizeClient(d.id, d.data())).sort((a, b) => a.fullName.localeCompare(b.fullName)));
  }

  async function reloadReferenceData() {
    await Promise.all([
      loadStaff().catch((error) => console.error('Staff load failed', error)),
      loadClients().catch((error) => console.error('Client load failed', error))
    ]);
  }

  useEffect(() => {
    loadInquiries().catch((error) => setMessage(error instanceof Error ? error.message : 'Unable to load inquiries.'));
    reloadReferenceData();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((item) =>
      (stage === 'all' || item.stage === stage) &&
      (!q || [
        item.inquiryCode,
        item.clientSnapshot?.name,
        item.clientName,
        item.phone,
        item.destination,
        item.serviceType,
        item.assignedUserName
      ].join(' ').toLowerCase().includes(q))
    );
  }, [items, search, stage]);

  const clientChoices = useMemo(() => {
    const q = clientSearch.toLowerCase();
    return clients
      .filter((client) => !q || [client.fullName, client.phone, client.email, client.usCity, client.indiaCity].join(' ').toLowerCase().includes(q))
      .slice(0, 80);
  }, [clients, clientSearch]);

  const staffChoices = useMemo(() => staffForDepartment(staffOptions, form.department || 'general'), [staffOptions, form.department]);

  function beginAdd() {
    setEditing(null);
    setForm(emptyForm);
    setClientSearch('');
    setMessage('');
    setOpen(true);
    reloadReferenceData();
  }

  function beginEdit(item: Inquiry) {
    setEditing(item);
    setForm(item);
    setClientSearch(item.clientSnapshot?.name || item.clientName || '');
    setMessage('');
    setOpen(true);
    reloadReferenceData();
  }

  function selectClient(clientId: string) {
    const client = clients.find((item) => item.id === clientId);
    if (!client) return;

    setForm({
      ...form,
      clientId: client.id,
      clientName: client.fullName,
      phone: client.phone,
      email: client.email,
      clientSnapshot: {
        name: client.fullName,
        phone: client.phone,
        email: client.email
      }
    });
    setClientSearch(client.fullName);
  }

  async function quickCreateClient() {
    const name = String(form.clientName || '').trim();
    const phone = String(form.phone || '').trim();

    if (!name) {
      setMessage('Client name is required before creating Client Master record.');
      return;
    }
    if (!phone) {
      setMessage('Phone is required before creating Client Master record.');
      return;
    }

    const parts = name.split(/\s+/);
    const firstName = parts[0] || name;
    const lastName = parts.slice(1).join(' ');

    const payload = {
      firstName,
      lastName,
      fullName: name,
      phoneCountryCode: '+1',
      phoneNumber: phone.replace(/^\+1/, ''),
      phoneFull: phone,
      email: form.email || '',
      usCity: '',
      indiaCity: '',
      label: 'general',
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: user.uid,
      updatedBy: user.uid
    };

    const ref = await addDoc(collection(db, 'clients'), payload);

    setForm({
      ...form,
      clientId: ref.id,
      clientName: name,
      phone,
      email: form.email || '',
      clientSnapshot: {
        name,
        phone,
        email: form.email || ''
      }
    });

    await loadClients();
    setMessage('Client created and linked to inquiry.');
  }

  async function save() {
    const err = validate(form);
    if (err) {
      setMessage(err);
      return;
    }

    if (!userCanSeeDepartment(user, form.department || 'general')) {
      setMessage('You are not allowed to create/assign inquiry for this department.');
      return;
    }

    setBusy(true);
    try {
      if (!editing) {
        const dup = await findDuplicateWarning(form);
        if (dup && !window.confirm(`Possible duplicate found: ${dup.inquiryCode} - ${dup.clientSnapshot.name}. Save anyway?`)) return;
        await createInquiry(form, user);
      } else {
        await updateInquiry(editing.id, form, user);
      }
      setOpen(false);
      await loadInquiries();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to save inquiry.');
    } finally {
      setBusy(false);
    }
  }

  async function quickStage(item: Inquiry, newStage: Inquiry['stage']) {
    try {
      if (newStage === 'converted') {
        await createBookingFromInquiry(item, user);
      } else {
        await setInquiryStage(item.id, newStage, user);
        await loadInquiries();
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to update stage.');
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">Phase 2.2 Integrated</p>
          <h1>CRM / Inquiry V2</h1>
          <p>Client-linked inquiry capture, department-based staff assignment, follow-ups, and booking conversion.</p>
        </div>
        <button className="btn primary" title="Add inquiry" onClick={beginAdd}><Plus size={18} /> Add Inquiry</button>
      </div>

      {message && <div className="error-box" style={{ marginBottom: 20 }}>{message}</div>}

      <div className="toolbar">
        <input placeholder="Search client, phone, destination, inquiry no" value={search} onChange={(event) => setSearch(event.target.value)} />
        <select value={stage} onChange={(event) => setStage(event.target.value)}>
          <option value="all">All stages</option>
          <option value="new">New</option>
          <option value="assigned">Assigned</option>
          <option value="follow_up">Follow-up</option>
          <option value="quoted">Quoted</option>
          <option value="waiting">Waiting</option>
          <option value="converted">Converted</option>
          <option value="not_converted">Not Converted</option>
        </select>
      </div>

      <section className="card table-card">
        <table>
          <thead>
            <tr>
              <th>Inquiry</th>
              <th>Client</th>
              <th>Service</th>
              <th>Travel</th>
              <th>Stage</th>
              <th>Follow-up</th>
              <th>Assigned</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="empty">No inquiries found.</td></tr>}
            {filtered.map((item) => (
              <tr key={item.id}>
                <td><strong>{item.inquiryCode}</strong><br /><small>{item.source}</small></td>
                <td>{item.clientSnapshot?.name || item.clientName || 'Unnamed Client'}<br /><small>{item.clientSnapshot?.phone || item.phone || '-'}</small></td>
                <td>{item.serviceType}<br /><small>{item.destination || '-'}</small></td>
                <td>{item.travelStartDate || '-'}<br /><small>{item.travelEndDate || item.returnDate || '-'}</small></td>
                <td><span className={`stage ${item.stage}`}>{item.stage.replace('_', ' ')}</span></td>
                <td>{item.nextFollowUpDate || '-'}</td>
                <td>{item.assignedUserName || '-'}</td>
                <td className="actions">
                  <button title="Edit inquiry" onClick={() => beginEdit(item)}><Edit size={16} /></button>
                  <button title="Convert inquiry to booking" disabled={item.stage === 'converted'} onClick={() => quickStage(item, 'converted')}><CheckCircle size={16} /></button>
                  <button title="Mark not converted" onClick={() => quickStage(item, 'not_converted')}><XCircle size={16} /></button>
                  <button title="Delete inquiry" onClick={async () => { if (confirm('Delete this inquiry?')) { await deleteInquiry(item.id); await loadInquiries(); } }}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {open && (
        <div className="modal-backdrop">
          <div className="modal large">
            <div className="modal-head">
              <h2>{editing ? 'Edit Inquiry' : 'Add Inquiry'}</h2>
              <button className="icon-only" title="Close" onClick={() => setOpen(false)}>×</button>
            </div>

            {message && <div className="error-box" style={{ margin: 20 }}>{message}</div>}

            <div className="form-section-title">Client Link</div>
            <div className="form-grid">
              <label className="span-2">Search Existing Client
                <input value={clientSearch} placeholder="Search name, phone, email, city" onChange={(event) => setClientSearch(event.target.value)} />
              </label>
              <label>Select Client
                <select value={form.clientId || ''} onChange={(event) => selectClient(event.target.value)}>
                  <option value="">Manual / New lead</option>
                  {clientChoices.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.fullName} — {client.phone || client.email || 'no contact'}
                    </option>
                  ))}
                </select>
              </label>

              <label>Client Name<input value={form.clientName || ''} onChange={(event) => setForm({ ...form, clientId: '', clientName: event.target.value })} /></label>
              <label>Phone<input value={form.phone || ''} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label>
              <label>Email<input value={form.email || ''} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
              <div className="span-3">
                <button type="button" className="btn" title="Create Client Master record from this inquiry" onClick={quickCreateClient}>
                  <Plus size={16} /> Quick Add New Client
                </button>
              </div>
            </div>

            <div className="form-section-title">Travel Requirement</div>
            <div className="form-grid">
              <label>Service Type
                <select value={form.serviceType || 'package'} onChange={(event) => setForm({ ...form, serviceType: event.target.value as Inquiry['serviceType'] })}>
                  <option value="package">package</option>
                  <option value="flight">flight</option>
                  <option value="cruise">cruise</option>
                  <option value="hotel">hotel</option>
                  <option value="visa">visa</option>
                  <option value="other">other</option>
                </select>
              </label>

              <label>Department
                <select value={form.department || 'package'} onChange={(event) => setForm({ ...form, department: event.target.value as Inquiry['department'], assignedUserId: '', assignedUserName: '' })}>
                  <option value="package">package</option>
                  <option value="flight">flight</option>
                  <option value="cruise">cruise</option>
                  <option value="account">account</option>
                  <option value="general">general</option>
                </select>
              </label>

              <label>Destination<input value={form.destination || ''} onChange={(event) => setForm({ ...form, destination: event.target.value })} /></label>
              <label>Travel Start Date<input type="date" value={form.travelStartDate || ''} onChange={(event) => setForm({ ...form, travelStartDate: event.target.value })} /></label>
              <label>Trip / Service End Date<input type="date" value={form.travelEndDate || ''} onChange={(event) => setForm({ ...form, travelEndDate: event.target.value })} /></label>
              <label>Return Flight Date<input type="date" value={form.returnDate || ''} onChange={(event) => setForm({ ...form, returnDate: event.target.value })} /></label>
              <label>Total Pax<input type="number" value={form.totalPax || 1} onChange={(event) => setForm({ ...form, totalPax: Number(event.target.value) })} /></label>
              <label>Budget<input type="number" value={form.budget || 0} onChange={(event) => setForm({ ...form, budget: Number(event.target.value) })} /></label>
              <label>Quoted Amount<input type="number" value={form.quotedAmount || 0} onChange={(event) => setForm({ ...form, quotedAmount: Number(event.target.value) })} /></label>
            </div>

            <div className="form-section-title">Sales Control</div>
            <div className="form-grid">
              <label>Source
                <select value={form.source || 'manual'} onChange={(event) => setForm({ ...form, source: event.target.value })}>
                  <option value="manual">manual</option>
                  <option value="whatsapp">whatsapp</option>
                  <option value="phone">phone</option>
                  <option value="website">website</option>
                  <option value="walk_in">walk_in</option>
                  <option value="referral">referral</option>
                  <option value="facebook">facebook</option>
                  <option value="instagram">instagram</option>
                  <option value="repeat_client">repeat_client</option>
                </select>
              </label>

              <label>Stage
                <select value={form.stage || 'new'} onChange={(event) => setForm({ ...form, stage: event.target.value as Inquiry['stage'] })}>
                  <option value="new">new</option>
                  <option value="assigned">assigned</option>
                  <option value="follow_up">follow_up</option>
                  <option value="quoted">quoted</option>
                  <option value="waiting">waiting</option>
                  <option value="converted">converted</option>
                  <option value="not_converted">not_converted</option>
                </select>
              </label>

              <label>Priority
                <select value={form.priority || 'medium'} onChange={(event) => setForm({ ...form, priority: event.target.value as Inquiry['priority'] })}>
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                  <option value="urgent">urgent</option>
                </select>
              </label>

              <label>Assigned To
                <select
                  value={form.assignedUserId || ''}
                  onChange={(event) => {
                    const selected = staffChoices.find((staff) => staff.uid === event.target.value);
                    setForm({
                      ...form,
                      assignedUserId: selected?.uid || '',
                      assignedUserName: selected?.fullName || '',
                      stage: selected?.uid && form.stage === 'new' ? 'assigned' : form.stage
                    });
                  }}
                >
                  <option value="">Select staff</option>
                  {staffChoices.map((staff) => (
                    <option key={staff.uid} value={staff.uid}>
                      {staff.fullName} — {staff.primaryDepartment} — {staff.role}
                    </option>
                  ))}
                </select>
              </label>

              <label>Next Follow-up<input type="date" value={form.nextFollowUpDate || ''} onChange={(event) => setForm({ ...form, nextFollowUpDate: event.target.value })} /></label>
              <label>Waiting Reason<input value={form.waitingReason || ''} onChange={(event) => setForm({ ...form, waitingReason: event.target.value })} /></label>
              <label className="span-3">Client Request Note<textarea value={form.clientRequestNote || ''} onChange={(event) => setForm({ ...form, clientRequestNote: event.target.value })} /></label>
              <label className="span-3">Internal Note<textarea value={form.internalNote || ''} onChange={(event) => setForm({ ...form, internalNote: event.target.value })} /></label>
            </div>

            <div className="modal-foot">
              <button className="btn" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn primary" onClick={save} disabled={busy}>{busy ? 'Saving...' : 'Save Inquiry'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
